/**
 * Dashboard mirror of the backend check-type registry
 * (quik-core-v2 src/app/v1/usecases/detection/rules/rule-spec.ts).
 *
 * The two must stay in step: the backend rejects anything this file lets
 * through, so a drift shows up as a save that fails with a message the form
 * never warned about. `describeRule` in particular is duplicated deliberately —
 * it renders live under the form as the author types, and the backend uses the
 * same wording to label stored results, so the sentence someone approved is the
 * sentence that appears on the card months later.
 */

export type DeterministicCheckType = 'TEXT_COMPARE' | 'NUMERIC_COMPARE' | 'DATE_COMPARE' | 'LOCATION_PROXIMITY' | 'FIELD_PRESENCE';

export type AiCheckType =
  | 'IMAGE_CONTAINS'
  | 'IMAGE_QUALITY'
  | 'DOCUMENT_FIELD_EXTRACT'
  | 'DOCUMENT_AUTHENTICITY'
  | 'SIGNATURE_PRESENT'
  | 'SEMANTIC_MATCH'
  | 'CUSTOM_AI';

export type CheckType = DeterministicCheckType | AiCheckType;

export type AiProviderId = 'claude' | 'openai' | 'gemini';

export type SlotKind = 'text' | 'number' | 'date' | 'location' | 'image';
export type ExpectPolarity = 'PASS_WHEN_TRUE' | 'PASS_WHEN_FALSE';
export type MissingInputBehaviour = 'inconclusive' | 'skip' | 'fail';

export interface RuleSourceConfig {
  sourceType:
    | 'step_text_field'
    | 'step_image_field'
    | 'step_location'
    | 'step_date_field'
    | 'step_submission_date'
    | 'resource_type_field'
    | 'custom';
  stepUid?: string;
  fieldUid?: string;
  resourceTypeId?: string;
  customValue?: string;
  customType?: 'text' | 'image';
}

export interface RuleInput {
  slot: string;
  source: RuleSourceConfig;
}

export interface RuleSpec {
  checkType: CheckType;
  inputs: RuleInput[];
  params: Record<string, any>;
  expect: ExpectPolarity;
  onMissingInput: MissingInputBehaviour;
  /** Tier C only. Absent means "whatever the deployment's default provider is". */
  ai?: { provider?: AiProviderId; model?: string; fallback?: 'auto' | 'none' };
  /** Tier C only. Below this, a verdict becomes Inconclusive for a human to look at. */
  minConfidence?: number;
}

export interface SlotDef {
  name: string;
  kind: SlotKind;
  label: string;
  hint?: string;
}

export interface Option {
  value: string;
  label: string;
}

export const TEXT_OPERATORS: Option[] = [
  { value: 'equals', label: 'is exactly' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'matches_regex', label: 'matches the pattern' }
];

export const NUMERIC_OPERATORS: Option[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'gt', label: 'is greater than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is at most' },
  { value: 'between', label: 'is between' }
];

export const DATE_OPERATORS: Option[] = [
  { value: 'before', label: 'is before' },
  { value: 'after', label: 'is after' },
  { value: 'within', label: 'is within' }
];

export const PROXIMITY_MODES: Option[] = [
  { value: 'within', label: 'is within' },
  { value: 'outside', label: 'is further than' }
];

export const PRESENCE_MODES: Option[] = [
  { value: 'present', label: 'has a value' },
  { value: 'absent', label: 'is empty' }
];

export const DISTANCE_UNITS: Option[] = [
  { value: 'm', label: 'metres' },
  { value: 'km', label: 'kilometres' },
  { value: 'mi', label: 'miles' }
];

export const DURATION_UNITS: Option[] = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' }
];

/** Source types that can plausibly carry each slot kind. Mirrors KIND_SOURCES server-side. */
export const SOURCES_FOR_KIND: Record<SlotKind, string[]> = {
  text: ['step_text_field', 'step_date_field', 'step_submission_date', 'resource_type_field', 'custom'],
  number: ['step_text_field', 'resource_type_field', 'custom'],
  date: ['step_date_field', 'step_submission_date', 'resource_type_field', 'custom'],
  location: ['step_location', 'custom'],
  image: ['step_image_field', 'resource_type_field', 'custom']
};

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  step_text_field: 'Step field',
  step_image_field: 'Step image',
  step_date_field: 'Step date field',
  step_location: 'Step location (GPS)',
  step_submission_date: 'Step submitted at',
  resource_type_field: 'Profile / resource field',
  custom: 'Fixed value'
};

export interface CheckTypeDef {
  type: CheckType;
  label: string;
  summary: string;
  /** Engine badge — sets expectations about determinism and cost before saving. */
  engineNote: string;
  slotsFor(params: Record<string, any>): SlotDef[];
  defaults(): Record<string, any>;
}

export const CHECK_TYPE_LIST: CheckTypeDef[] = [
  {
    type: 'TEXT_COMPARE',
    label: 'Text comparison',
    summary: 'Compare a text value against another field or a fixed string.',
    engineNote: 'Exact match — no AI',
    slotsFor: () => [
      { name: 'value', kind: 'text', label: 'Text to check' },
      { name: 'expected', kind: 'text', label: 'Compare against', hint: 'Choose Fixed value to type a literal.' }
    ],
    defaults: () => ({ operator: 'equals', caseSensitive: false, trim: true })
  },
  {
    type: 'NUMERIC_COMPARE',
    label: 'Number comparison',
    summary: 'Over, under, equal to, or between bounds.',
    engineNote: 'Exact match — no AI',
    slotsFor: (params) =>
      params?.operator === 'between'
        ? [
            { name: 'value', kind: 'number', label: 'Value to check' },
            { name: 'min', kind: 'number', label: 'Lower bound' },
            { name: 'max', kind: 'number', label: 'Upper bound' }
          ]
        : [
            { name: 'value', kind: 'number', label: 'Value to check' },
            { name: 'compareTo', kind: 'number', label: 'Compare against' }
          ],
    defaults: () => ({ operator: 'gte', tolerance: 0 })
  },
  {
    type: 'DATE_COMPARE',
    label: 'Date comparison',
    summary: 'Ordering or elapsed time between two dates — expiry, deadlines, timeliness.',
    engineNote: 'Exact match — no AI',
    slotsFor: (params) => {
      const slots: SlotDef[] = [{ name: 'date', kind: 'date', label: 'Date to check' }];
      if (params?.referenceMode !== 'now') slots.push({ name: 'reference', kind: 'date', label: 'Compare against' });
      return slots;
    },
    defaults: () => ({ operator: 'before', referenceMode: 'source', offset: null })
  },
  {
    type: 'LOCATION_PROXIMITY',
    label: 'Location proximity',
    summary: 'How far apart two captured locations are.',
    engineNote: 'Exact match — no AI',
    slotsFor: () => [
      { name: 'pointA', kind: 'location', label: 'First location' },
      { name: 'pointB', kind: 'location', label: 'Second location' }
    ],
    defaults: () => ({ mode: 'within', maxDistance: { value: 20, unit: 'km' } })
  },
  {
    type: 'FIELD_PRESENCE',
    label: 'Field filled in',
    summary: 'Check a field was filled in — or deliberately left empty.',
    engineNote: 'Exact match — no AI',
    slotsFor: () => [{ name: 'value', kind: 'text', label: 'Field to check' }],
    defaults: () => ({ require: 'present' })
  }
];

// ── Tier C — checks that genuinely need a model ─────────────────────────────
// Mirrors ai-check-types.ts in quik-core-v2. The prompt itself is compiled
// server-side from these params; the author never writes it.

export const STRICTNESS_OPTIONS: Option[] = [
  { value: 'exact', label: 'say the same thing exactly' },
  { value: 'equivalent', label: 'mean the same thing' },
  { value: 'related', label: 'are about the same thing' }
];

export const QUALITY_CRITERIA: Option[] = [
  { value: 'legible', label: 'text is readable' },
  { value: 'in_focus', label: 'in focus, not blurred' },
  { value: 'fully_in_frame', label: 'subject fully in frame' },
  { value: 'adequate_lighting', label: 'well lit, no heavy glare' },
  { value: 'unobstructed', label: 'nothing covering the subject' }
];

const AI_ENGINE_NOTE = 'Uses AI';

export const AI_CHECK_TYPE_LIST: CheckTypeDef[] = [
  {
    type: 'IMAGE_CONTAINS',
    label: 'Photo shows…',
    summary: 'Check a photo does (or does not) show something specific.',
    engineNote: AI_ENGINE_NOTE,
    slotsFor: () => [{ name: 'image', kind: 'image', label: 'Photo' }],
    defaults: () => ({ subject: '', mustBeAbsent: false, minConfidence: 70 })
  },
  {
    type: 'IMAGE_QUALITY',
    label: 'Photo is usable',
    summary: 'Check a photo is good enough to rely on.',
    engineNote: AI_ENGINE_NOTE,
    slotsFor: () => [{ name: 'image', kind: 'image', label: 'Photo' }],
    defaults: () => ({ criteria: ['legible', 'in_focus', 'fully_in_frame'], minConfidence: 70 })
  },
  {
    type: 'DOCUMENT_FIELD_EXTRACT',
    label: 'Read a value off a document',
    summary: 'Pull one field out of a document photo — the value is shown on the result.',
    engineNote: AI_ENGINE_NOTE,
    slotsFor: () => [{ name: 'document', kind: 'image', label: 'Document photo' }],
    defaults: () => ({ documentType: 'document', field: '', minConfidence: 70 })
  },
  {
    type: 'DOCUMENT_AUTHENTICITY',
    label: 'Document looks genuine',
    summary: 'Look for signs a document has been altered.',
    engineNote: AI_ENGINE_NOTE,
    slotsFor: () => [{ name: 'document', kind: 'image', label: 'Document photo' }],
    defaults: () => ({ documentType: 'document', signals: [], minConfidence: 80 })
  },
  {
    type: 'SIGNATURE_PRESENT',
    label: 'Signed',
    summary: 'Check a document carries a signature.',
    engineNote: AI_ENGINE_NOTE,
    slotsFor: () => [{ name: 'document', kind: 'image', label: 'Document photo' }],
    defaults: () => ({ requireLegible: false, minConfidence: 70 })
  },
  {
    type: 'SEMANTIC_MATCH',
    label: 'Two values agree',
    summary: 'Compare two values by meaning rather than by characters.',
    engineNote: AI_ENGINE_NOTE,
    slotsFor: () => [
      { name: 'valueA', kind: 'text', label: 'First value' },
      { name: 'valueB', kind: 'text', label: 'Second value' }
    ],
    defaults: () => ({ strictness: 'equivalent', minConfidence: 70 })
  },
  {
    type: 'CUSTOM_AI',
    label: 'Custom instruction (advanced)',
    summary: 'Write your own instruction. Flexible, but results vary more than a structured check.',
    engineNote: 'Uses AI — results can vary',
    // Each slot declares its own kind — most hand-written checks are about a
    // photo, so forcing text would make this a worse escape hatch than the
    // legacy prompt form it replaces. Plain strings still accepted for rules
    // written before per-slot kinds.
    slotsFor: (params) =>
      (params?.slots?.length ? params.slots : [{ name: 'input1', kind: 'text' }]).map((entry: any) => {
        const name = typeof entry === 'string' ? entry : entry?.name;
        const kind = typeof entry === 'string' ? 'text' : entry?.kind === 'image' ? 'image' : 'text';
        return { name: String(name || 'input'), kind: kind as SlotKind, label: String(name || 'input') };
      }),
    defaults: () => ({ instruction: '', slots: [{ name: 'input1', kind: 'text' }], minConfidence: 70 })
  }
];

export const AI_CHECK_TYPES: Record<AiCheckType, CheckTypeDef> = AI_CHECK_TYPE_LIST.reduce(
  (acc, def) => ({ ...acc, [def.type]: def }),
  {} as Record<AiCheckType, CheckTypeDef>
);

export const isAiCheck = (t: CheckType | undefined): boolean => !!t && t in AI_CHECK_TYPES;

export const CHECK_TYPES: Record<CheckType, CheckTypeDef> = [...CHECK_TYPE_LIST, ...AI_CHECK_TYPE_LIST].reduce(
  (acc, def) => ({ ...acc, [def.type]: def }),
  {} as Record<CheckType, CheckTypeDef>
);

/** Deterministic types only — what the "add a rule" card offers first. */
export const DETERMINISTIC_CHECK_TYPES = CHECK_TYPE_LIST;

export function slotsForSpec(spec: Pick<RuleSpec, 'checkType' | 'params'> | null | undefined): SlotDef[] {
  if (!spec) return [];
  const def = CHECK_TYPES[spec.checkType];
  return def ? def.slotsFor(spec.params || {}) : [];
}

export function emptySpec(checkType: CheckType): RuleSpec {
  const def = CHECK_TYPES[checkType];
  const params = def.defaults();
  return {
    checkType,
    params,
    expect: 'PASS_WHEN_TRUE',
    onMissingInput: 'inconclusive',
    inputs: def.slotsFor(params).map((slot) => ({
      slot: slot.name,
      source: { sourceType: (SOURCES_FOR_KIND[slot.kind][0] as any) || 'custom' }
    }))
  };
}

const labelOf = (options: Option[], value: any, fallback: string) => options.find((o) => o.value === value)?.label || fallback;

/** Mirrors describeRule() in rule-spec.ts. Keep the wording identical. */
export function describeRule(spec: RuleSpec | null | undefined, slotNames: Record<string, string> = {}): string {
  if (!spec) return '';
  const p = spec.params || {};
  const n = (slot: string) => slotNames[slot] || slot;
  let condition: string;

  switch (spec.checkType) {
    case 'TEXT_COMPARE': {
      const op = labelOf(TEXT_OPERATORS, p.operator, 'is exactly');
      condition = `${n('value')} ${op} ${n('expected')}${p.caseSensitive ? ' (case-sensitive)' : ''}`;
      break;
    }
    case 'NUMERIC_COMPARE': {
      condition =
        p.operator === 'between'
          ? `${n('value')} is between ${n('min')} and ${n('max')}`
          : `${n('value')} ${labelOf(NUMERIC_OPERATORS, p.operator, 'equals')} ${n('compareTo')}`;
      break;
    }
    case 'DATE_COMPARE': {
      const op = labelOf(DATE_OPERATORS, p.operator, 'is before');
      const reference = p.referenceMode === 'now' ? 'the time the rule runs' : n('reference');
      const offset = p.offset?.value ? `${p.offset.value} ${labelOf(DURATION_UNITS, p.offset.unit, p.offset.unit)}` : '';
      condition =
        p.operator === 'within'
          ? `${n('date')} is within ${offset || '?'} of ${reference}`
          : `${n('date')} ${op} ${reference}${offset ? ` + ${offset}` : ''}`;
      break;
    }
    case 'LOCATION_PROXIMITY': {
      const d = p.maxDistance || {};
      condition = `${n('pointA')} ${labelOf(PROXIMITY_MODES, p.mode, 'is within')} ${d.value ?? '?'} ${labelOf(
        DISTANCE_UNITS,
        d.unit,
        d.unit || 'km'
      )} of ${n('pointB')}`;
      break;
    }
    case 'FIELD_PRESENCE': {
      condition = `${n('value')} ${labelOf(PRESENCE_MODES, p.require, 'has a value')}`;
      break;
    }
    // ── AI tier ──────────────────────────────────────────────────────────
    // Mirrors describeAiCondition() in ai-check-types.ts. Without these the
    // live echo under the form — the thing that catches an inverted rule —
    // would read "the configured condition holds" for every AI check.
    case 'IMAGE_CONTAINS':
      condition = p.mustBeAbsent
        ? `${n('image')} does not show ${p.subject || 'the subject'}`
        : `${n('image')} shows ${p.subject || 'the subject'}`;
      break;
    case 'IMAGE_QUALITY': {
      const wanted = (p.criteria || []).map((c: string) => QUALITY_CRITERIA.find((q) => q.value === c)?.label || c).join(', ');
      condition = `${n('image')} is ${wanted || 'usable'}`;
      break;
    }
    case 'DOCUMENT_FIELD_EXTRACT':
      condition = `the ${p.field || 'field'} can be read from ${n('document')}`;
      break;
    case 'DOCUMENT_AUTHENTICITY':
      condition = `${n('document')} looks like a genuine, unaltered ${p.documentType || 'document'}`;
      break;
    case 'SIGNATURE_PRESENT':
      condition = `${n('document')} carries a${p.requireLegible ? ' legible' : ''} signature`;
      break;
    case 'SEMANTIC_MATCH':
      condition = `${n('valueA')} and ${n('valueB')} ${labelOf(STRICTNESS_OPTIONS, p.strictness, 'mean the same thing')}`;
      break;
    case 'CUSTOM_AI': {
      const instruction = String(p.instruction || '').trim();
      condition = !instruction ? 'the custom instruction holds' : instruction.length > 120 ? `${instruction.slice(0, 117)}…` : instruction;
      break;
    }
    default:
      condition = 'the configured condition holds';
  }

  return spec.expect === 'PASS_WHEN_TRUE'
    ? `Match when ${condition} — otherwise No Match.`
    : `No Match when ${condition} — otherwise Match.`;
}

const STEP_BOUND = ['step_text_field', 'step_image_field', 'step_location', 'step_date_field', 'step_submission_date'];

/**
 * Client-side mirror of validateRuleSpec(). Same messages, so the form and the
 * save response never contradict each other.
 */
export function validateSpec(spec: RuleSpec | null | undefined): string[] {
  if (!spec) return ['The rule is empty.'];
  const problems: string[] = [];
  const p = spec.params || {};

  if (spec.checkType === 'DATE_COMPARE') {
    const hasOffset = p.offset && p.offset.value !== undefined && p.offset.value !== null && p.offset.value !== '';
    if (hasOffset && (isNaN(Number(p.offset.value)) || Number(p.offset.value) <= 0)) {
      problems.push('The time window must be a positive number.');
    }
    if (!hasOffset && p.operator === 'within') problems.push('"is within" needs a time window.');
  }
  if (spec.checkType === 'LOCATION_PROXIMITY') {
    const d = p.maxDistance || {};
    if (isNaN(Number(d.value)) || Number(d.value) <= 0) problems.push('Enter a distance greater than zero.');
  }
  if (spec.checkType === 'NUMERIC_COMPARE' && p.tolerance !== undefined && p.tolerance !== null && p.tolerance !== '') {
    if (isNaN(Number(p.tolerance)) || Number(p.tolerance) < 0) problems.push('Tolerance must be zero or a positive number.');
  }

  const bySlot = new Map((spec.inputs || []).map((i) => [i.slot, i]));
  for (const slot of slotsForSpec(spec)) {
    const source = bySlot.get(slot.name)?.source;
    if (!source?.sourceType) {
      problems.push(`${slot.label}: pick where the value comes from.`);
      continue;
    }
    if (['step_text_field', 'step_image_field', 'step_date_field'].includes(source.sourceType)) {
      if (!source.stepUid || !source.fieldUid) problems.push(`${slot.label}: choose both a step and a field.`);
    } else if (['step_location', 'step_submission_date'].includes(source.sourceType)) {
      if (!source.stepUid) problems.push(`${slot.label}: choose a step.`);
    } else if (source.sourceType === 'resource_type_field') {
      if (!source.resourceTypeId || !source.fieldUid) problems.push(`${slot.label}: choose both a resource type and a field.`);
    } else if (source.sourceType === 'custom') {
      if (!String(source.customValue ?? '').trim()) problems.push(`${slot.label}: enter a value.`);
      if (spec.checkType === 'TEXT_COMPARE' && p.operator === 'matches_regex' && slot.name === 'expected') {
        try {
          // eslint-disable-next-line no-new
          new RegExp(String(source.customValue ?? ''));
        } catch {
          problems.push(`"${source.customValue}" is not a valid pattern.`);
        }
      }
    }
  }

  const bound = slotsForSpec(spec)
    .map((s) => bySlot.get(s.name)?.source?.sourceType)
    .filter(Boolean) as string[];
  if (bound.length > 0 && !bound.some((t) => STEP_BOUND.includes(t))) {
    problems.push(
      'At least one input must come from a workflow step — a rule is only checked when a step it uses is submitted, so this one would never run.'
    );
  }

  return problems;
}

/** Keep `inputs` aligned with the slots the current params imply. */
export function reconcileInputs(spec: RuleSpec): RuleSpec {
  const slots = slotsForSpec(spec);
  const bySlot = new Map((spec.inputs || []).map((i) => [i.slot, i]));
  return {
    ...spec,
    inputs: slots.map(
      (slot) =>
        bySlot.get(slot.name) || {
          slot: slot.name,
          source: { sourceType: (SOURCES_FOR_KIND[slot.kind][0] as any) || 'custom' }
        }
    )
  };
}
